import { useState } from "react";
import api from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { InfoTip } from "@/components/InfoTip";
import { Mail, Download, Loader2, Info, Eye } from "lucide-react";

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return d; } };

export function ForwardingNotice({ propertyId, address, landlordName, moveOutDate }) {
  const { isPremium, openUpgrade } = useTier();
  const { user } = useAuth();
  const [form, setForm] = useState({
    tenant_name: user?.name || "",
    forwarding_address: "",
    move_out_date: moveOutDate || "",
    landlord_name: landlordName || "",
  });
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);

  const valid = form.tenant_name.trim() && form.forwarding_address.trim();

  const download = async () => {
    if (!valid) return;
    setLoading(true);
    try {
      const res = await api.post(`/properties/${propertyId}/forwarding-notice`, form, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "forwarding-address-notice.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Forwarding notice downloaded");
    } catch {
      toast.error("Could not generate the notice. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6" data-testid="forwarding-notice-card">
      <div className="flex items-center gap-2">
        <Mail size={20} className="text-brand" />
        <h2 className="font-heading text-xl font-semibold text-navy">Forwarding address notice</h2>
        <InfoTip topic="forwardingNotice" />
      </div>
      <p className="mt-2 text-sm text-[#475569]">
        Generate a formal written notice telling your landlord where to send your deposit and any statement.
      </p>

      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-medium text-navy">Your name</Label>
          <Input data-testid="fwd-tenant-name" value={form.tenant_name}
            onChange={(e) => setForm({ ...form, tenant_name: e.target.value })} className="mt-1.5 h-11 rounded-xl" />
        </div>
        <div>
          <Label className="text-sm font-medium text-navy">Landlord name (optional)</Label>
          <Input data-testid="fwd-landlord-name" value={form.landlord_name}
            onChange={(e) => setForm({ ...form, landlord_name: e.target.value })} className="mt-1.5 h-11 rounded-xl" />
        </div>
        <div>
          <Label className="text-sm font-medium text-navy">Move-out date (optional)</Label>
          <Input data-testid="fwd-moveout-date" type="date" value={form.move_out_date}
            onChange={(e) => setForm({ ...form, move_out_date: e.target.value })} className="mt-1.5 h-11 rounded-xl" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-sm font-medium text-navy">Forwarding address</Label>
          <Textarea data-testid="fwd-address" value={form.forwarding_address}
            onChange={(e) => setForm({ ...form, forwarding_address: e.target.value })}
            placeholder="55 New Street, Apt 2&#10;Newtown, CA 90210" className="mt-1.5 rounded-xl" rows={3} />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <Button data-testid="fwd-preview-btn" variant="outline" disabled={!valid}
          onClick={() => setShowPreview((v) => !v)} className="rounded-xl h-11">
          <Eye size={16} className="mr-2" /> {showPreview ? "Hide preview" : "Preview"}
        </Button>
        <Button data-testid="fwd-download-btn" disabled={!valid || loading} onClick={download}
          className="rounded-xl h-11 bg-navy hover:bg-navy-hover text-white">
          {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />}
          Download PDF
        </Button>
      </div>

      {showPreview && valid && (
        <div data-testid="fwd-preview" className="mt-4 rounded-xl border border-[#E2E8F0] bg-[#FAFAF9] p-5 text-sm text-[#334155] leading-relaxed">
          <p className="font-heading font-semibold text-navy">Notice of Forwarding Address</p>
          <p className="mt-3 text-xs text-[#94A3B8]">{fmtDate(new Date())}</p>
          <p className="mt-2">To: {form.landlord_name || "Landlord/Property Manager"}</p>
          <p>Re: Former tenancy at {address}</p>
          <p className="mt-3">Dear {form.landlord_name || "Landlord/Property Manager"},</p>
          <p className="mt-2">I am writing to provide my forwarding address following my move-out from the property located at {address}{form.move_out_date ? ` on ${fmtDate(form.move_out_date)}` : ""}.</p>
          <p className="mt-2">Please send any correspondence relating to my former tenancy — including the return of my security deposit and any itemized statement of deductions — to the address below:</p>
          <p className="mt-2 whitespace-pre-line"><b>{form.tenant_name}</b>{"\n"}{form.forwarding_address}</p>
          <p className="mt-2">Please update your records accordingly. Thank you.</p>
          <p className="mt-3">Sincerely,<br />{form.tenant_name}</p>
        </div>
      )}

      <div className="mt-4 flex items-start gap-2 rounded-xl bg-amber-soft border border-amber-border px-4 py-3 text-xs text-[#334155]">
        <Info size={15} className="mt-0.5 shrink-0 text-amber-ink" />
        <span>Send this by certified mail with return receipt and keep the receipt with your evidence. This is a document-preparation tool, not legal advice.</span>
      </div>

      {!isPremium && (
        <button data-testid="fwd-upsell-link" onClick={() => openUpgrade("moveout_plan")}
          className="mt-3 text-sm text-brand hover:underline">
          Protect the whole move-out — see the Move-Out Countdown Plan →
        </button>
      )}
    </div>
  );
}
